import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import apiRequest from '../../services/api';
import { Plus, Pencil, Trash2 } from 'lucide-react';

const styles = {
  container: { maxWidth: '900px', margin: '0 auto', padding: '24px' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  title: { fontSize: '22px', fontWeight: '600', margin: 0 },
  card: { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' },
  btn: { padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '14px' },
  btnPrimary: { backgroundColor: '#3b82f6', color: '#fff' },
  btnSecondary: { backgroundColor: '#f3f4f6', color: '#374151' },
  btnDanger: { backgroundColor: '#ef4444', color: '#fff' },
  btnSmall: { padding: '4px 10px', fontSize: '12px' },
  input: { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', width: '100%' },
  placeholder: { textAlign: 'center', padding: '48px 0', color: '#6b7280' },
};

export default function IntentTypeManagement() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [intentTypes, setIntentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editCode, setEditCode] = useState(null);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleNavigate = (_navId, path) => {
    if (path) { navigate(path); }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchIntentTypes = useCallback(async () => {
    try {
      const res = await apiRequest('/api/v1/admin/intent-types');
      if (res.success) {
        const types = res.data || [];
        const withUsage = await Promise.all(types.map(async (t) => {
          try {
            const usageRes = await apiRequest(`/api/v1/admin/intent-types/${t.code}/usage`);
            return { ...t, usage: usageRes.success ? usageRes.data : { total: null, unknown: true } };
          } catch {
            return { ...t, usage: { total: null, unknown: true } };
          }
        }));
        setIntentTypes(withUsage);
      }
    } catch {
      // handled
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchIntentTypes(); }, [fetchIntentTypes]);

  const handleToggle = async (code, currentEnabled) => {
    try {
      await apiRequest(`/api/v1/admin/intent-types/${code}/toggle`, {
        method: 'PATCH',
        body: { enabled: !currentEnabled },
      });
      setIntentTypes((prev) => prev.map((t) => t.code === code ? { ...t, enabled: !currentEnabled } : t));
      showToast(`${code} ${!currentEnabled ? 'enabled' : 'disabled'}`);
    } catch {
      showToast('Toggle failed', 'error');
    }
  };

  const handleDelete = async (code) => {
    setDeleteConfirm(null);
    try {
      await apiRequest(`/api/v1/admin/intent-types/${code}`, { method: 'DELETE' });
      setIntentTypes((prev) => prev.filter((t) => t.code !== code));
      showToast(`${code} deleted`);
    } catch (err) {
      showToast(err.message || 'Delete failed', 'error');
    }
  };

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="campaigns"
      onNavigate={handleNavigate}
      user={{ name: authUser?.fullName || 'Admin', email: authUser?.email || '', avatar: authUser?.profilePicture || null }}
    >
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <h1 style={styles.title}>Intent Types</h1>
          <button
            style={{ ...styles.btn, ...styles.btnPrimary, display: 'flex', alignItems: 'center', gap: '4px' }}
            onClick={() => setShowCreate(true)}
          >
            <Plus size={14} /> New Intent Type
          </button>
        </div>

        <div style={styles.card}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: '500', color: '#6b7280', fontSize: '13px' }}>Code</th>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: '500', color: '#6b7280', fontSize: '13px' }}>Description</th>
                <th style={{ textAlign: 'center', padding: '10px 16px', fontWeight: '500', color: '#6b7280', fontSize: '13px' }}>Status</th>
                <th style={{ textAlign: 'center', padding: '10px 16px', fontWeight: '500', color: '#6b7280', fontSize: '13px' }}>Usage</th>
                <th style={{ textAlign: 'right', padding: '10px 16px', fontWeight: '500', color: '#6b7280', fontSize: '13px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ ...styles.placeholder, padding: '32px 0' }}>Loading...</td></tr>
              ) : intentTypes.length === 0 ? (
                <tr><td colSpan={5} style={{ ...styles.placeholder, padding: '32px 0' }}>No intent types found.</td></tr>
              ) : (
                intentTypes.map((it) => (
                  <tr key={it.code} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 16px' }}>
                      <code style={{ fontSize: '13px', fontWeight: '500' }}>{it.code}</code>
                    </td>
                    <td style={{ padding: '10px 16px', color: '#374151' }}>
                      {editCode === it.code ? (
                        <EditDescriptionInline
                          code={it.code}
                          initialDescription={it.description}
                          onSave={(desc) => {
                            setIntentTypes((prev) => prev.map((t) => t.code === it.code ? { ...t, description: desc } : t));
                            setEditCode(null);
                            showToast('Description updated');
                          }}
                          onCancel={() => setEditCode(null)}
                        />
                      ) : (
                        it.description
                      )}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                      <button
                        onClick={() => handleToggle(it.code, it.enabled)}
                        title={it.enabled ? 'Click to disable' : 'Click to enable'}
                        style={{
                          width: '40px', height: '22px', borderRadius: '11px', border: 'none',
                          backgroundColor: it.enabled ? '#10b981' : '#d1d5db',
                          position: 'relative', cursor: 'pointer', transition: 'background-color 0.2s',
                        }}
                      >
                        <span style={{
                          position: 'absolute', top: '2px',
                          left: it.enabled ? '20px' : '2px',
                          width: '18px', height: '18px', borderRadius: '50%',
                          backgroundColor: '#fff', transition: 'left 0.2s',
                        }} />
                      </button>
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                      {it.usage?.unknown ? <span style={{ color: '#d97706', fontStyle: 'italic' }}>Unknown</span> : (it.usage?.total ?? 0)}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                      <span style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                        <button
                          style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnSecondary, display: 'flex', alignItems: 'center', gap: '4px' }}
                          onClick={() => setEditCode(it.code)}
                          title="Edit description"
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          style={{
                            ...styles.btn, ...styles.btnSmall, ...styles.btnDanger,
                            display: 'flex', alignItems: 'center', gap: '4px',
                            opacity: (it.usage?.unknown || (it.usage?.total ?? 0) > 0) ? 0.4 : 1,
                            cursor: (it.usage?.unknown || (it.usage?.total ?? 0) > 0) ? 'not-allowed' : 'pointer',
                          }}
                          disabled={it.usage?.unknown || (it.usage?.total ?? 0) > 0}
                          title={it.usage?.unknown ? 'Cannot delete — usage unknown' : (it.usage?.total ?? 0) > 0 ? `Cannot delete — used by ${it.usage.total} code(s)` : 'Delete'}
                          onClick={() => setDeleteConfirm(it.code)}
                        >
                          <Trash2 size={12} />
                        </button>
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {toast && (
        <div style={{
          position: 'fixed', top: '16px', right: '16px', padding: '12px 20px',
          borderRadius: '8px',
          backgroundColor: toast.type === 'error' ? '#fecaca' : toast.type === 'warning' ? '#fef3c7' : '#d1fae5',
          color: toast.type === 'error' ? '#dc2626' : toast.type === 'warning' ? '#d97706' : '#065f46',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1001, fontSize: '14px',
        }}>
          {toast.message}
        </div>
      )}

      {showCreate && (
        <CreateIntentTypeDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            fetchIntentTypes();
            showToast('Intent type created');
          }}
        />
      )}

      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '400px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: '600' }}>Delete Intent Type</h3>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#6b7280' }}>
              Are you sure you want to delete <code>{deleteConfirm}</code>? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button style={{ ...styles.btn, ...styles.btnDanger }} onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function EditDescriptionInline({ code, initialDescription, onSave, onCancel }) {
  const [desc, setDesc] = useState(initialDescription);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiRequest(`/api/v1/admin/intent-types/${code}`, {
        method: 'PUT',
        body: { description: desc },
      });
      onSave(desc);
    } catch {
      // handled
    } finally {
      setSaving(false);
    }
  };

  return (
    <span style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
      <input
        style={{ ...styles.input, fontSize: '13px' }}
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        autoFocus
      />
      <button style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnPrimary }} onClick={handleSave} disabled={saving}>
        {saving ? '...' : 'Save'}
      </button>
      <button style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnSecondary }} onClick={onCancel}>
        Cancel
      </button>
    </span>
  );
}

function CreateIntentTypeDialog({ onClose, onCreated }) {
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleCodeChange = (e) => {
    setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''));
  };

  const handleSubmit = async () => {
    if (!code.trim()) { setError('Code is required'); return; }
    if (!description.trim()) { setError('Description is required'); return; }
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiRequest('/api/v1/admin/intent-types', {
        method: 'POST',
        body: { code: code.trim(), description: description.trim() },
      });
      if (res.success) { onCreated(); }
    } catch (err) {
      setError(err.message || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '480px' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '600' }}>New Intent Type</h3>

        {error && <div style={{ padding: '8px 12px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>{error}</div>}

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Code *</label>
          <input
            style={styles.input}
            value={code}
            onChange={handleCodeChange}
            placeholder="e.g., GUIDE_SIGNUP"
          />
          <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Uppercase letters, digits, and underscores only</p>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Description *</label>
          <textarea
            style={{ ...styles.input, minHeight: '60px', resize: 'vertical' }}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Human-readable description of this intent type"
          />
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={onClose}>Cancel</button>
          <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
